import { getGa4Context, isGa4TrackingEnabled, sendGa4Signup, trackGa4Purchase } from './ga4';
import client from './axiosInstance';
import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import { getCookie, deleteCookie, getPersistedValue, setPersistedValue } from './utils';
import {
  MARKETPLACE_COHORT_SETTING_KEY,
  MARKETPLACE_EXPERIMENT_ID,
  MARKETPLACE_EXPERIMENT_SETTING_KEY,
} from './marketplaceExperiment';
import { getBillingPeriodExperimentContext } from './billingPeriodExperiment';


const normalizeUuidString = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue || !uuidValidate(normalizedValue)) {
    return null;
  }

  return normalizedValue;
};

const getCheckoutAttributionContext = () => {
  const anonymousIdFromCookie = normalizeUuidString(getPersistedValue('cr_aid', 'cr_aid'));
  const anonymousIdFromAnalytics = normalizeUuidString(Analytics.anonymousId);
  const sessionIdFromCookie = normalizeUuidString(getPersistedValue('_crate_sid', '_crate_sid'));

  return {
    anonymous_id: anonymousIdFromCookie || anonymousIdFromAnalytics,
    session_id: sessionIdFromCookie,
  };
};

/**
 * @typedef {Object} SubscriptionRequestPayload
 * @property {string} provider
 * @property {number|string} plan_id
 * @property {number[]=} addon_price_ids
 * @property {string=} anonymous_id
 * @property {string=} session_id
 */
const buildSubscriptionRequestPayload = ({ provider, planId, addonPriceIds = [], addonIds = [], nativeApp = null }) => {
  const payload = /** @type {SubscriptionRequestPayload} */ ({
    provider,
    plan_id: planId,
  });

  const resolvedAddonPriceIds = Array.isArray(addonPriceIds) && addonPriceIds.length > 0
    ? addonPriceIds
    : (Array.isArray(addonIds) ? addonIds : []);

  if (resolvedAddonPriceIds.length > 0) {
    payload.addon_price_ids = resolvedAddonPriceIds;
  }
  if (typeof nativeApp === 'string' && nativeApp.trim()) {
    payload.native_app = nativeApp.trim();
  }
  const { anonymous_id, session_id } = getCheckoutAttributionContext();

  if (anonymous_id) {
    payload.anonymous_id = anonymous_id;
  }

  if (session_id) {
    payload.session_id = session_id;
  }

  const experimentContext = getBillingPeriodExperimentContext();
  if (experimentContext) {
    payload.experiment_context = experimentContext;
  }

  return payload;
};

export const forgetPassword = async (data) => {
  try{
    const response = await client.post('auth/forgot-password', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resetPassword = async (data) => {
  try {
    const response = await client.post('auth/reset-password',  data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const loginUserFn = async (user) => {
  try {
    const response = await client.post('auth/login', user);
    return response.data;
  } catch (error) {
    throw error;
  }
}

export const magicLinkLoginFn = async (linkToken) => {
  try {
    const response = await client.post('auth/magic', { link_token: linkToken });
    return response.data;
  } catch (error) {
    throw error;
  }
}

export const refreshTokenRequest = async (refresh_token) => {
  try {
    const response = await client.post('auth/refresh-token', {refresh_token});
    return response.data;
  } catch (error) {
    throw error;
  }
}

export const continuePortalLoginFn = async () => {
  const response = await client.post('auth/portal/continue');
  return response.data;
}
export const deleteUserFn = async () => {
  const response = await client.delete('me')
  return response.data
}


export const subscribeUserFn = async ({ provider, planId, addonPriceIds, addonIds, nativeApp }) => {
  const response = await client.post('subscriptions', { ...buildSubscriptionRequestPayload({ provider, planId, addonPriceIds, addonIds, nativeApp }), ...(await getGa4Context()) })
  if(response && response.data){
    return response.data
  }
  console.log("bad response from subscribe [provider, planId, addonPriceIds, response]", provider, planId, addonPriceIds, response);
  return false;
}

export const checkoutBundleFn = async ({ provider, bundleId, bundleLicenseTierId, redirectUrl, nativeApp }) => {
  const payload = {
    bundle_id: bundleId,
    provider,
  };
  const parsedBundleLicenseTierId = Number(bundleLicenseTierId);
  if (Number.isFinite(parsedBundleLicenseTierId) && parsedBundleLicenseTierId > 0) {
    payload.bundle_license_tier_id = parsedBundleLicenseTierId;
  }
  if (typeof redirectUrl === 'string' && redirectUrl.trim()) {
    payload.redirect_url = redirectUrl.trim();
  }
  if (typeof nativeApp === 'string' && nativeApp.trim()) {
    payload.native_app = nativeApp.trim();
  }

  const response = await client.post('bundles/checkout', { ...payload, ...(await getGa4Context()) });

  if (response && response.data) {
    return response.data;
  }

  console.log("bad response from bundle checkout [provider, bundleId, bundleLicenseTierId, redirectUrl, response]", provider, bundleId, bundleLicenseTierId, redirectUrl, response);
  return false;
}

export const createNativeAppTicketFn = async (clientId) => {
  const response = await client.post(`native-apps/${encodeURIComponent(clientId)}/ticket`);
  return response.data;
}

export const resumeSubscriptionFn = async ({ provider, planId }) => {
  const response = await client.post('subscriptions/resume', {
    provider,
    plan_id: planId,
  })
  if(response && response.data){
    return response.data
  }
  console.log("bad response from resume subscription [provider, planId, response]", provider, planId, response);
  return false;
}

export const pauseSubscriptionFn = async (payload) => {
  if(payload?.resumes_at && payload?.resumes_in_days){
    throw new Error("Invalid pause payload: cannot send resumes_at and resumes_in_days together.");
  }

  const hasPayload = !!(payload && Object.keys(payload).length > 0);
  const response = hasPayload
    ? await client.post('subscriptions/pause', payload)
    : await client.post('subscriptions/pause');

  if(response && response.data){
    return response.data
  }
  console.log("bad response from pause subscription [payload, response]", payload, response);
  return false;
}

export const cancelSubscription = async () => {
  const response = await client.delete('subscriptions');
  if(response && response.data){
    return response.data;
  }
  console.log("bad response from cancel subscription", response);
  return false;
  
}

export const confirmSubscriptionCancellation = async (subscriptionId) => {
  const response = await client.post('subscriptions/confirm-cancellation', {
    subscription_id: subscriptionId,
  });
  return response.data;
}

export const createStripePaymentMethodUpdateLink = async () => {
  const response = await client.post('subscriptions/payment-method-update');
  return response.data;
}

export const retryStripePastDuePayment = async () => {
  const response = await client.post('subscriptions/retry-past-due-payment');
  return response.data;
}

export const getProducts = async ({ campaign, paymentLinkId } = {}) => {
  const searchParams = new URLSearchParams();
  if (campaign) {
    searchParams.set('campaign_id', campaign);
  }
  if (paymentLinkId) {
    searchParams.set('payment_link_id', paymentLinkId);
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/products?${queryString}` : '/products';
  const response = await client.get(url);

  if(response && response.data){
    return response.data;
  }
  return false;
}

export const getCampaignBundles = async ({ campaign } = {}) => {
  const searchParams = new URLSearchParams();
  if (campaign) {
    searchParams.set('campaign_id', campaign);
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/campaigns/bundles?${queryString}` : '/campaigns/bundles';
  const response = await client.get(url);
  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  return [];
}

export const getBundlesFn = async () => {
  const response = await client.get('bundles');
  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return [];
}

export const getBundleBySlugFn = async (bundleSlug) => {
  const normalizedBundleSlug = String(bundleSlug ?? '').trim();
  if (!normalizedBundleSlug) {
    return null;
  }

  const response = await client.get(`bundles/${encodeURIComponent(normalizedBundleSlug)}`);
  const bundle = response?.data;
  const bundleId = Number(bundle?.id);

  return Number.isFinite(bundleId) && bundleId > 0 ? bundle : null;
}

export const getPurchasedBundlesFn = async () => {
  const response = await client.get('bundles/purchased');
  const responseData = response?.data;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return [];
}

const normalizeMarketplaceCollectionResponse = (responseData) => {
  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return [];
}

const parseMarketplaceCartVersion = (value) => {
  const parsedValue = Number(value);
  if (!Number.isInteger(parsedValue) || parsedValue < 0) {
    return 0;
  }

  return parsedValue;
};

const normalizeMarketplaceCartResponse = (responseData) => {
  const checkoutUrl = typeof responseData?.checkout_url === 'string' && responseData.checkout_url.trim()
    ? responseData.checkout_url.trim()
    : null;
  const version = parseMarketplaceCartVersion(responseData?.version);

  if (responseData && typeof responseData === 'object' && responseData.cart && typeof responseData.cart === 'object') {
    return {
      cart: responseData.cart,
      version,
      checkout_url: checkoutUrl,
    };
  }

  if (responseData && typeof responseData === 'object' && (
    Array.isArray(responseData.items)
      || Object.prototype.hasOwnProperty.call(responseData, 'subtotal_amount')
      || Object.prototype.hasOwnProperty.call(responseData, 'currency')
      || Object.prototype.hasOwnProperty.call(responseData, 'id')
  )) {
    return {
      cart: responseData,
      version,
      checkout_url: checkoutUrl,
    };
  }

  return {
    cart: null,
    version,
    checkout_url: checkoutUrl,
  };
};

const buildMarketplaceCartMutationConfig = ({ version, idempotencyKey }) => ({
  headers: {
    'If-Match': `${Number.isInteger(version) && version >= 0 ? version : 0}`,
    'Idempotency-Key': String(idempotencyKey ?? ''),
  },
});

export const getMarketplaceOrdersFn = async () => {
  const response = await client.get('marketplace/orders');
  return normalizeMarketplaceCollectionResponse(response?.data);
}

export const getMarketplacePurchasedAssetsFn = async () => {
  const response = await client.get('marketplace/purchases/assets');
  return normalizeMarketplaceCollectionResponse(response?.data);
}

export const getMarketplaceCartFn = async () => {
  const response = await client.get('marketplace/cart');
  return normalizeMarketplaceCartResponse(response?.data);
}

export const addMarketplaceCartItemFn = async (payload, { version, idempotencyKey } = {}) => {
  const response = await client.post(
    'marketplace/cart/items',
    payload,
    buildMarketplaceCartMutationConfig({ version, idempotencyKey })
  );
  return normalizeMarketplaceCartResponse(response?.data);
}

export const removeMarketplaceCartItemFn = async (lineId, { version, idempotencyKey } = {}) => {
  const response = await client.delete(
    `marketplace/cart/items/${lineId}`,
    buildMarketplaceCartMutationConfig({ version, idempotencyKey })
  );
  return normalizeMarketplaceCartResponse(response?.data);
}

export const checkoutMarketplaceCartFn = async ({
  version,
  idempotencyKey,
  provider = 'stripe',
} = {}) => {
  const response = await client.post(
    'marketplace/cart/checkout',
    {
      provider,
      ...(await getGa4Context()),
    },
    buildMarketplaceCartMutationConfig({ version, idempotencyKey })
  );
  return { ...normalizeMarketplaceCartResponse(response?.data), ...(response?.data?.analytics ? { analytics: response.data.analytics } : {}) };
}

export const getPlanAddonsFn = async (planId) => {
  if (!planId && planId !== 0) {
    return [];
  }

  const response = await client.get(`plans/${planId}/addons`);
  const responseData = response?.data;
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  if (Array.isArray(responseData?.addons)) {
    return responseData.addons;
  }
  return [];
}

export const addSubscriptionAddonFn = async (addonPriceId) => {
  const response = await client.post('subscriptions/addons', { addon_price_id: addonPriceId });
  if (response && response.data) {
    return response.data;
  }
  return true;
}

export const removeSubscriptionAddonFn = async (addonId) => {
  const response = await client.delete(`subscriptions/addons/${addonId}`);
  if (response && response.data) {
    return response.data;
  }
  return true;
}

export const getWebBanners = async ({ location } = {}) => {
  const params = {};
  if (location) {
    params.location = location;
  }

  const response = await client.get('web/banners', { params });

  if (response && response.data) {
    return response.data;
  }
  return [];
}


export const favoritesFn = async (data) => {
  let response = {};
  let favorites = [];

  if(data){
    response = await client.get('me');
    if(response.data.settings && response.data.settings['favorites']){
      favorites = JSON.parse(response.data.settings['favorites']);
    }
    if(favorites.filter(item => item.id === data.id).length === 0){
      favorites.push(data);
      response = await client.put('me/settings', {favorites: JSON.stringify(favorites)});

      return response.data;
    }
    return favorites;

  }else{
    response = await client.get('me');
    if(response.data.settings && response.data.settings['favorites']){
      favorites = JSON.parse(response.data.settings['favorites']);
    }
    return favorites;
  }
}

export const favoritesDelete = async id => {
  let favorites = await favoritesFn();
  favorites = favorites.filter(item => item.id !== id);
  const response = await client.put('me/settings', {favorites: JSON.stringify(favorites)});
  return favorites;
}

export const userFn = async (data) => {
  let response = {};
  try{
    if(data){
      response = await client.put('me', data);
    }else{
      response = await client.get('me');
    }
    return response.data;
  } catch (error) {
    console.log("Throw error", error.message);
    //throw error;
  }
}

export const updateUserProfileFn = async (data) => {
  const response = await client.put('me', data);
  return response.data;
}

export const meFn = async (data) => {
  let response = {};
  try{
    if(data){
      response = await client.put('me/settings', data);
    }else{
      response = await client.get('me');
    }
    return response.data;
  } catch (error) {
    console.log("Throw error", error.message);
    //throw error;
  }
}

export const expireAllSessions = async () => {
  let response = {};
  try{
    response = await client.post('me/expire-session');
    return response.data;
  } catch (error) {
    console.log("Throw error", error.message);
    //throw error;
  }
}



/*
Members (GET): Listado de miembros del teams al cual pertenece el user
Delete Member (DELETE): Borra un miembro por users_id (solo el owner puede borrar, no puedo borrarse a si mismo)
Invitations
Invitations (GET): Invitaciones enviadas
Invite (POST): Crea una nueva invitación (esto no crea usuarios)
Delete invitation (DELETE)
Resend Invitation (GET): Reenvia el mail de invitación
Accept Invite (GET): Este método lo usas para confirmar una invitación. Tenes que mandar el token que lo recibis como parametro desde el mail. 
*/

export const teamFn = {
  settings: async (data) => {
    let response = {};
      if(data){
        response = await client.put('team/settings', data);
      }else{
        response = await client.get('team/settings');
      }
    return response.data
  },
  seats: async (seatContext = null) => {
    const endpoint = seatContext ? `team/seats?seat_context=${encodeURIComponent(seatContext)}` : 'team/seats';
    const response = await client.get(endpoint);
    return response.data;
  },
  revokeSeat: async (seatId) => {
    const response = await client.post(`team/seats/${seatId}/revoke`);
    return response.data;
  },
  appLicenses: {
    list: async () => {
      const response = await client.get('team/app-licenses');
      return response.data;
    },
    updateAssignments: async (teamBundleId, appId, userIds) => {
      const response = await client.put(
        `team/bundles/${encodeURIComponent(teamBundleId)}/apps/${encodeURIComponent(appId)}/license-assignments`,
        {user_ids: userIds}
      );
      return response.data;
    },
    checkoutUpgrade: async (teamBundleId, bundleLicenseTierId, provider) => {
      const response = await client.post(
        `team/bundles/${encodeURIComponent(teamBundleId)}/license-upgrades/checkout`,
        {
          bundle_license_tier_id: bundleLicenseTierId,
          provider,
        }
      );
      return response.data;
    },
  },
  members: {
    list: async () => {
      let response = {};
      response = await client.get('team/members');
      return response.data
    },
    delete: async (id) => {
      let response = await client.delete(`team/members/${id}`);
      return response.data
    },
  },
  invitations: {
    list: async () => {
      let response = {};
      response = await client.get('team/invitations');
      return response.data
    },
    delete: async (id) => {
      let response = await client.delete(`team/invite/${id}`);
      return response.data
    },
    invite: async (email) => {
      let response = await client.post(`team/invite`, {email});
      return response.data
    },
    resend: async (id) => {
      let response = await client.get(`team/invite/resend/${id}`);
      return response.data
    },
    accept: async (key) => {
      let response = await client.get(`team/invite/accept/${key}`);
      return response.data
    },
    getEmail:  async (key) => {
      let response = await client.get(`team/invite/get-email/${key}`);
      return response.data
    }
  }


}

/*
export const teamFn = async (data) => {
  let response = {};
    if(data){
      response = await client.put('team/settings', data);
    }else{
      response = await client.get('team/settings');
    }
  return response.data
}
*/

export const getInvoices = async () => {
  const response = await client.get('invoices')
  return response.data
}

export const getAssetsAvailability = async () => {
  const response = await client.get('assets/remaining')
  return response.data
}

const normalizeAssetsConsumptionsResponse = (payload) => {
  if (Array.isArray(payload)) {
    const total = payload.length;
    return {
      data: payload,
      links: {},
      meta: {
        current_page: 1,
        from: total ? 1 : null,
        last_page: 1,
        per_page: total,
        to: total || null,
        total,
      },
    };
  }

  return {
    ...payload,
    data: Array.isArray(payload?.data) ? payload.data : [],
    links: payload?.links && typeof payload.links === 'object' ? payload.links : {},
    meta: payload?.meta && typeof payload.meta === 'object' ? payload.meta : {},
  };
};

export const getAssetsConsumptions = async ({ page = 1, startDate, endDate } = {}) => {
  const params = { page };

  if (startDate) {
    params['filter[start_date]'] = startDate;
  }

  if (endDate) {
    params['filter[end_date]'] = endDate;
  }

  const response = await client.get('assets/consumptions', { params });
  return normalizeAssetsConsumptionsResponse(response.data);
}

export const consumeAssets = async (data) => {
    const response = await client.post('assets/consume', data);
    
    return response.data;
}

export const resendVerificationEmail = async () => {
  const response = await client.get('/email/resend');
  return response.data;
}

const normalizeAnnouncementImage = (announcement) => {
  const rawImage = announcement?.image ?? announcement?.image_url ?? null;
  if (!rawImage) {
    return null;
  }

  if (typeof rawImage === 'string') {
    return {
      url: rawImage,
      sizes: {
        sm: rawImage,
        md: rawImage,
        lg: rawImage,
      },
    };
  }

  const sizeMap = rawImage?.sizes && typeof rawImage.sizes === 'object'
    ? rawImage.sizes
    : {};
  const fallbackUrl = rawImage?.url ?? rawImage?.sm ?? rawImage?.md ?? rawImage?.lg ?? null;

  return {
    url: rawImage?.url ?? fallbackUrl,
    sizes: {
      sm: sizeMap.sm ?? rawImage?.sm ?? fallbackUrl,
      md: sizeMap.md ?? rawImage?.md ?? fallbackUrl,
      lg: sizeMap.lg ?? rawImage?.lg ?? fallbackUrl,
    },
  };
};

const normalizeAnnouncement = (announcement) => {
  const readAt = announcement?.read_at
    ?? announcement?.state?.read_at
    ?? announcement?.user_state?.read_at
    ?? null;
  const createdAt = announcement?.created_at ?? announcement?.published_at ?? null;
  const publishedAt = announcement?.published_at ?? createdAt;

  return {
    ...announcement,
    id: announcement?.id,
    title: announcement?.title ?? '',
    message: announcement?.message ?? '',
    type: announcement?.type ?? 'announcement',
    url: announcement?.url ?? null,
    image: normalizeAnnouncementImage(announcement),
    read_at: readAt,
    created_at: createdAt,
    published_at: publishedAt,
    expires_at: announcement?.expires_at ?? null,
  };
};

const getAnnouncementTimestamp = (announcement) => {
  const publishedAt = Date.parse(announcement?.published_at || '');
  if (Number.isFinite(publishedAt) && publishedAt > 0) {
    return publishedAt;
  }

  const createdAt = Date.parse(announcement?.created_at || '');
  if (Number.isFinite(createdAt) && createdAt > 0) {
    return createdAt;
  }

  return 0;
};

const normalizeAnnouncementsResponse = (payload) => {
  const announcements = Array.isArray(payload?.data) ? payload.data : [];
  const normalizedAnnouncements = announcements
    .map(normalizeAnnouncement)
    .sort((a, b) => getAnnouncementTimestamp(b) - getAnnouncementTimestamp(a));

  return {
    ...payload,
    data: normalizedAnnouncements,
    meta: payload?.meta ?? {},
  };
};

export const getNotifications = async ({ page = 1 } = {}) => {
  const response = await client.get('inbox/announcements', { params: { page } });
  return normalizeAnnouncementsResponse(response.data);
}

export const markNotificationAsRead = async (notificationId) => {
  const response = await client.post(`inbox/announcements/${notificationId}/read`);
  return response.data;
}

export const dismissNotification = async (notificationId) => {
  const response = await client.delete(`inbox/announcements/${notificationId}`);
  return response.data;
}

export const dismissAllNotifications = async () => {
  const response = await client.delete('inbox/announcements');
  return response.data;
}

export const searchAssets = async (search, search_type=false, loggedIn) => {
    var query = search
    if (query === null || query === '') {
      query = '';
    } else {
      query = encodeURIComponent(query).replace(/%20/g, "+");
    }
    
    const locationOrigin = window.location.origin || 'https://www.productioncrate.com';;
    let uriPath = locationOrigin;

    if(!search_type){
      search_type = 'all';
    }

    switch (search_type) {
        case '3d':
            uriPath = 'https://rendercrate.com';
            break;
        case 'graphics':
            uriPath = 'https://graphicscrate.com';
            break;
        case 'vfx':
            uriPath = 'https://footagecrate.com';
            break;
        case 'sfx':
            uriPath = 'https://soundscrate.com';
            break;
    }
    if (loggedIn) {
        uriPath = 'https://www.productioncrate.com';
    }
    if(locationOrigin.includes('localhost')){
        uriPath = locationOrigin;
    }
    if(locationOrigin.includes('user-qa.') || locationOrigin.includes('staging.')) {
      uriPath = 'https://staging.productioncrate.com';
    }
    
    let search_url = `${uriPath}/search/${query}?main_category=${search_type}`;
    window.location.href = search_url;
    return false;
}
export const providerLoginFn = async (provider, credentials) => {
  let url = `auth/${provider}/login`;
  if(provider === 'email'){
      url = `auth/register`;
  }
  try{
    let hasReferral = getCookie('referral');
    if(hasReferral){
      let reference = JSON.parse(decodeURIComponent(hasReferral));

      credentials['referral'] = reference['referral'] || '';
      credentials['referral_data'] = reference['referral_data'] || {};
    }

    const response = await client.post(url, credentials);
    sendGa4Signup(response.data?.analytics?.signup);
    return response.data;
  } catch (error) {
    console.log("RESPONSE", error);
    throw error;
  }
}

export const formatErrorResponse = (response) => {
  return response?.data?.error ?? response?.data?.message ?? response?.statusText ?? 'Something went wrong'
}

export const deleteFaultyCookies = () => {
  try {
    if(
      getCookie("cr_sess_refresh_local") ||
      getCookie("cr_sess_local") 

      //DEPRACATED LOCALSTORAGE
      /*
      // || localStorage.getItem("cr_sess_refresh_local") ||
      // localStorage.getItem("cr_sess_local")
      // */
    ){  
      console.log("user has faulty cookies, deleting...");
      deleteCookie("cr_sess_refresh_local");
      deleteCookie("cr_sess_local");

      //DEPRACATED LOCALSTORAGE
      //localStorage.removeItem("cr_sess_refresh_local");
      //localStorage.removeItem("cr_sess_local");
      Analytics.sendCustomEvent('deleteFaultyCookies');
    }
  } catch (error) {
    console.error("Error trying to delete cookies", error);
  }
  
};

const getEnvironment = () => window.location.hostname === "localhost" ? "development" : window.location.hostname === "user-qa.productioncrate.com" ? "qa" : "production";

const safeExecute = async (fn, errorMessage) => {
  try {
    return await fn();
  } catch (error) {
    console.error(errorMessage, error);
  }
};

const partnerStackSignUp = data => {
  safeExecute(() => {
    if (typeof window.growsumo !== 'object') {
      console.log("Partner Stack wasn't initialized.");
      return;
    }
    let hasReferral = getCookie('referral');
    if (!hasReferral) {
      console.log("Signup without partnerstack referrer.");
      return;
    }
    window.growsumo.data = {
      name: data.name || '',
      email: data.email || '',
      customer_key: `${data.team_id || ''}`
    };
    window.growsumo.createSignup();
    deleteCookie('referral', '.productioncrate.com');
  }, "Error in partnerStackSignUp");
};

const sendGAEvent = (event_name, paid_amount, additional_data = {}) => {
  safeExecute(() => {
    if (getEnvironment() === 'development') {
      console.log('event', event_name, {
        'send_to': 'G-78KP68EXMW',
        'value': paid_amount,
        'currency': 'USD',
        ...additional_data
      });
      return;
    }
    if (window.gtag) {
      window.gtag('event', event_name, {
        'send_to': 'G-78KP68EXMW',
        'value': paid_amount,
        'currency': 'USD',
        ...additional_data
      });
    }
  }, "Error in sendGAEvent");
};

const sendConversion = (send_to, paid_amount) => {
  safeExecute(() => {
    if (getEnvironment() === 'development') {
      console.log('event', 'conversion', {
        'send_to': send_to,
        'value': paid_amount,
        'currency': 'USD'
      });
      return;
    }
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': send_to,
        'value': paid_amount,
        'currency': 'USD'
      });
    }
  }, "Error in sendConversion");
};

const dataLayerPush = (event_name, data) => {
  safeExecute(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ 'event': event_name, ...data });
  }, "Error in dataLayerPush");
};

const normalizeCohort = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');
const isValidCohort = (value) => value === 'A' || value === 'B';
const isExperimentFlag = (value) => value === '1' || value === 1 || value === true || value === 'true';

export const Analytics = {
  anonymousId: null,
  userSettings: null,

  setUserSettings: (settings) => {
    Analytics.userSettings = settings && typeof settings === 'object' ? settings : null;
  },

  init: () => {
    let existingId = getPersistedValue('cr_aid', 'cr_aid');
    if (!existingId) {
        existingId = uuidv4();
        setPersistedValue('cr_aid', 'cr_aid', existingId);
    }
    Analytics.anonymousId = existingId;
  },

  sendCustomEvent: (event, metadata={}) => {
    return safeExecute(async () => {
      if (!Analytics.anonymousId) {
        Analytics.init();
      }
      const anonymous_id = Analytics.anonymousId;
      const settings = Analytics.userSettings && typeof Analytics.userSettings === 'object' ? Analytics.userSettings : null;
      const settingsMarketplaceCohort = normalizeCohort(settings?.[MARKETPLACE_COHORT_SETTING_KEY]);
      const cookieMarketplaceCohort = normalizeCohort(
        getPersistedValue(`cr_exp_${MARKETPLACE_EXPERIMENT_ID}_bucket`, `cr_exp_${MARKETPLACE_EXPERIMENT_ID}_bucket`)
      );
      const cohort_marketplace_v1 = isValidCohort(settingsMarketplaceCohort)
        ? settingsMarketplaceCohort
        : (isValidCohort(cookieMarketplaceCohort) ? cookieMarketplaceCohort : null);

      const settingsMarketplaceExperiment = settings?.[MARKETPLACE_EXPERIMENT_SETTING_KEY];
      const cookieMarketplaceExperiment = getPersistedValue(
        `cr_exp_${MARKETPLACE_EXPERIMENT_ID}_enrolled`,
        `cr_exp_${MARKETPLACE_EXPERIMENT_ID}_enrolled`
      );
      const cohort_marketplace_v1_experiment = isExperimentFlag(settingsMarketplaceExperiment)
        ? '1'
        : (isExperimentFlag(cookieMarketplaceExperiment) ? '1' : null);

      const cohortMetadata = {};
      if (cohort_marketplace_v1) {
        cohortMetadata.cohort_marketplace_v1 = cohort_marketplace_v1;
      }
      if (cohort_marketplace_v1_experiment) {
        cohortMetadata.cohort_marketplace_v1_experiment = cohort_marketplace_v1_experiment;
      }

      const billingPeriodExperiment = getBillingPeriodExperimentContext();
      if (billingPeriodExperiment) {
        cohortMetadata.billing_period_experiment = billingPeriodExperiment;
      }

      console.debug("Analytics.sendCustomEvent - Anonymous ID:", anonymous_id, event, metadata);
      try{
        await client.post('analytics/event', {
          event,
          anonymous_id,
          metadata: {
            path: window.location.pathname,
            host: window.location.hostname,
            href: window.location.href,
            referrer: document.referrer,
            user_agent: navigator.userAgent,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
            viewport_size: `${window.innerWidth}x${window.innerHeight}`,
            ...cohortMetadata,
            ...metadata
          }
        });
      } catch (error) {
        console.log("Error in Analytics.sendCustomEvent", error);
      }
    }, "Error in Analytics.sendCustomEvent: "+event);
  },
  view: (name, metadata={}) => {
    return Analytics.sendCustomEvent(`view_${name}`, metadata);
  },
  click: (name) => {
    return Analytics.sendCustomEvent('click', {target: name});
  },

  signup: (paid_amount = 0, userData) => {
    safeExecute(() => {
      sendConversion('AW-709072149/9-m3CK6M-a8YEJWqjtIC', paid_amount);
      if (!isGa4TrackingEnabled()) sendGAEvent('sign_up', paid_amount);
      partnerStackSignUp(userData);
      Analytics.sendCustomEvent('signup', {paid_amount, ...userData});
    }, "Error in Analytics.signup");
  },
  addToCart: (paid_amount = 0, plan_name = "") => {
    safeExecute(() => {
      sendConversion('AW-709072149/wmV6CLLtgbAYEJWqjtIC', paid_amount);
      sendGAEvent('plan_selection', paid_amount);
      Analytics.sendCustomEvent('addToCart', {paid_amount, plan_name});
    }, "Error in Analytics.addToCart");
  },
  conversion: (paid_amount = 0, subscriptionId = false) => {
    safeExecute(() => {
      sendConversion('AW-709072149/FZ5WCNL8768YEJWqjtIC', paid_amount);
      if (isGa4TrackingEnabled()) {
        trackGa4Purchase(client);
      } else {
        sendGAEvent('purchase', paid_amount, { signed_on: true, paying_member: true, page_title: 'New Pro Member' });
      }
      dataLayerPush('purchaseConversion', {
        'conversionValue': paid_amount,
        'subscriptionId': subscriptionId
      });
      Analytics.sendCustomEvent('conversion', {paid_amount, subscriptionId});
    }, "Error in Analytics.conversion");
  }
};

export const trackConfirmedGa4Purchase = () => trackGa4Purchase(client);
